import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { produce } from 'immer';
import { Grid, Paper, List, ListSubheader, Button, Stack } from "@mui/material";
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody"
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Tooltip from "@mui/material/Tooltip";
import toastr from "toastr";
import _ from "lodash";
import dayjs from 'dayjs';
import {
  FormTitleTypography, FormTableRow, FormTableCell,
  SectorTitleTypography, DataEntryTextField, FormHeaderPaper,
  CustomizedDivider
} from "../../../components/CustomStyles/StyledComponents";
import CustomizableCancelModal from "../../../components/Modal/CustomizableCancelModal";
import { toastrCustomOptions, toastrCustomOptionsLonger } from "../../../constants/toastrOptions";
import { addCommas, removeCommas } from "../../../helpers/stringFormatHelper";
import { getFirstVisibleCellDetailsInDataEntry } from '../../../helpers/dataEntryHelper';
import {
  getMonthlyStockData,
  setMonthlyStockData,
  resetMonthlyStockData,
  saveMonthlyStockData,
  resetResponseState,
  unsetLoading,
  setLoading
} from "../../../store/appAction";
import SelectedFarm from '../../../components/SelectedFarm/SelectedFarm';
import SelectedReport from '../../../components/SelectedReport/SelectedReport';
import queryString from 'query-string';

const RH_SX = { width: '20%' };
const AVG_SX = { width: '8%', whiteSpace: 'pre-line' };
const MONTHS_SX = { width: '6%', padding: '1px 1px !important' };
const MONTHS = [
  { m: 'month1', sx: MONTHS_SX, header: 'Prev. year closing total', tooltip: 'Livestock numbers at 1st of this month' },
  { m: 'month2', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at at end of month' },
  { m: 'month3', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at end of month' },
  { m: 'month4', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at end of month' },
  { m: 'month5', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at end of month' },
  { m: 'month6', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at end of month' },
  { m: 'month7', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at end of month' },
  { m: 'month8', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at end of month' },
  { m: 'month9', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at end of month' },
  { m: 'month10', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at end of month' },
  { m: 'month11', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at end of month' },
  { m: 'month12', sx: MONTHS_SX, header: '', tooltip: 'Livestock numbers at end of month' }
]

const calculateRowAvg = (row) => {
  let lineTotal = MONTHS.reduce((acc, currMonth) => acc + parseFloat(row[currMonth.m]), 0)
  let avg = parseFloat(lineTotal / 12).toFixed(2);
  return avg
}

const DataEntryTable = ({ key, sector, initialFormData, firstVisibleCellDetails, locked, sectorIndex }) => {
  toastr.options = toastrCustomOptions;
  const dispatch = useDispatch();
  const rptYearEnd = useSelector(state => state.farmReport.reportSettings.yearEnd);
  const [sectorDataInt, setSectorDataInt] = useState(undefined);
  const [sectorDataExt, setSectorDataExt] = useState(undefined);

  const startDate = dayjs(rptYearEnd).startOf('month');
  const monthsArray = Array.from({ length: 12 }, (_, i) => {
    return dayjs(startDate).subtract(i, 'months').format('MMM\nYYYY');
  }).reverse();

  useEffect(() => {
    setSectorDataInt(initialFormData);
    setSectorDataExt(initialFormData);
  }, [initialFormData]);

  useEffect(() => {
    if (sectorDataExt && !_.isEmpty(sectorDataExt)) {
      dispatch(setMonthlyStockData({
        sectorId: sector.sectorId,
        formData: sectorDataExt
      }));
    }
  }, [dispatch, sector.sectorId, sectorDataExt]);


  const handleInputChange = useCallback((
    e,
    sectorId,
    groupId,
    esItemId,
    stockId,
    stock,
    stockKey,
    rowData,
    esGroupIndex,
    esItemIndex,
    calculatedAverage
  ) => {
    let value = parseFloat(removeCommas(e.target.value) || 0);
    const previousRowData = { ...rowData };
    previousRowData[stockKey] = value;
    let average = calculateRowAvg(previousRowData)
    if (e.target.inputMode === "numeric") {
      let numericRegex = /^[0-9]*(\.[0-9]{0,2})?$/;
      if (numericRegex.test(value)) {
        setSectorDataInt(produce((draft) => {
          draft.esGroups[esGroupIndex].esItems[esItemIndex].stock[stockKey] = value;
          draft.esGroups[esGroupIndex].esItems[esItemIndex].stock['average'] = average;
        }));
        setSectorDataExt(produce((draft) => {
          draft.esGroups[esGroupIndex].esItems[esItemIndex].stock[stockKey] = value;
          draft.esGroups[esGroupIndex].esItems[esItemIndex].stock['average'] = average;
        }));
      }

    }
  }, []);

  const getCellContent = ({ sectorId, groupId, esItemId, stockId, stock, stockKey, rowData, disabled, esGroupIndex, esItemIndex }) => {
    let calculatedAverage = 0.00;
    if (rowData) {
      calculatedAverage = calculateRowAvg(rowData)
    }
    return (
      <DataEntryTextField
        disabled={disabled}
        size="small"
        variant="outlined"
        slotProps={{ htmlInput: {inputMode:"numeric", sx:{textAlign: "right", padding:'1px 1px'}} }}
        sx={{ width: '95%', padding: '1px 1px !important', }}
        value={(stockKey === 'average' ? (calculatedAverage) : rowData && addCommas(rowData[stockKey])) || ''}
        onChange={(e) =>
          handleInputChange(
            e,
            sectorId,
            groupId,
            esItemId,
            stockId,
            stock,
            stockKey,
            rowData,
            esGroupIndex,
            esItemIndex,
            calculatedAverage
          )
        }
      />
    )
  };
  // /Average number year (no)
  return (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      {(sector.esGroups || []).map((esGroup, esGroupIndex) => (
        <Fragment>
          <SectorTitleTypography sx={{ mb: 2 }}>
            {sector.sectorName} - {esGroup.groupName}
          </SectorTitleTypography>
          <Table
            sx={{ minWidth: 700 }}
            size="small"
            aria-label="customized table"
          >
            <TableHead>
              <FormTableRow>
                <FormTableCell sx={RH_SX}></FormTableCell>
                {
                  MONTHS.map((month) => {
                    return <FormTableCell key={month.m} sx={month.sx}>{month.header}</FormTableCell>
                  })
                }
                <FormTableCell sx={AVG_SX}>Audit year average</FormTableCell>
              </FormTableRow>
              <FormTableRow>
                <FormTableCell sx={RH_SX}></FormTableCell>
                {
                  monthsArray?.map((month) => {
                    return (<FormTableCell key={month} sx={{ width: '1%', whiteSpace: 'pre-line' }}>{month}</FormTableCell>)
                  })
                }
                <FormTableCell sx={AVG_SX}></FormTableCell>
              </FormTableRow>
            </TableHead>

            <TableBody>
              {
                esGroup.esItems.map((esItem, esItemIndex) => {
                  return (
                    <FormTableRow>
                      <FormTableCell sx={RH_SX}>{esItem.itemName}</FormTableCell>
                      {
                        MONTHS.map((month) => {
                          return <FormTableCell key={month.m} sx={month.sx}>
                            <Tooltip title={month.tooltip} variant='inputdetails'>{
                            getCellContent({
                              sectorId: sector.sectorId,
                              groupId: esGroup.groupId,
                              esItemId: esItem.itemId,
                              stockId: esItem.stock.id,
                              stock: esItem.stock,
                              stockKey: month.m,
                              rowData: sectorDataInt?.esGroups[esGroupIndex]?.esItems[esItemIndex]?.stock,
                              disabled: locked,
                              esGroupIndex: esGroupIndex,
                              esItemIndex: esItemIndex
                            })}
                            </Tooltip>
                          </FormTableCell>
                        })
                      }
                      <FormTableCell sx={AVG_SX}>
                        <Tooltip title='Average number of livestock over audit year' variant='inputdetails'>{
                          getCellContent({
                            sectorId: sector.sectorId,
                            groupId: esGroup.groupId,
                            esItemId: esItem.itemId,
                            stockId: esItem.stock.id,
                            stock: esItem.stock,
                            stockKey: 'average',
                            rowData: sectorDataInt?.esGroups[esGroupIndex]?.esItems[esItemIndex]?.stock,
                            disabled: locked,
                            esGroupIndex: esGroupIndex,
                            esItemIndex: esItemIndex
                          })}
                        </Tooltip>
                      </FormTableCell>
                    </FormTableRow>
                  )
                })
              }
            </TableBody>
          </Table>
        </Fragment>
      ))}
    </TableContainer>
  );
};

const MonthlyStockTool = (props) => {
  const dispatch = useDispatch();
  let navigate = useNavigate();

  const reportId = useSelector(state => state.common.reportId);
  const initialFormData = useSelector(state => state.monthlyStock.initialFormData);
  const { successMsg, errorMsg } = useSelector(state => state.common);
  const { reportName, locked } = useSelector(state => state.farmReport.reportSettings);
  const [formData, setFormData] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [firstVisibleCellDetails, setFirstVisibleCellDetails] = useState(null);

  const sectors = initialFormData?.sectors || [];

  useEffect(() => {
    dispatch(setLoading());
    dispatch(getMonthlyStockData({ reportId }));
    dispatch(resetResponseState());
    return () => {
      dispatch(resetMonthlyStockData());
    }
  }, [dispatch, reportId]);

  useEffect(() => {
    const tempData = { ...initialFormData };
    setFormData(tempData);
    setTimeout(() => {
      dispatch(unsetLoading());
    }, 500);
  }, [dispatch, initialFormData]);


  useEffect(() => {
    if (successMsg || errorMsg) {
      dispatch(unsetLoading());
      successMsg && toastr.success(successMsg);
      errorMsg && toastr.error(errorMsg, undefined, toastrCustomOptionsLonger);
      dispatch(resetResponseState());
      if (successMsg) {
        dispatch(resetMonthlyStockData());
        navigate(`/data-entry/livestock/numbers-weights?${queryString.stringify({ monthlyStockTool: true })}`);
      }
    }
  }, [dispatch, navigate, successMsg, errorMsg]);

  useEffect(() => {
    const firstVisibleCellDetails = getFirstVisibleCellDetailsInDataEntry(sectors);
    setFirstVisibleCellDetails(firstVisibleCellDetails);
  }, [sectors]);

  const handleModalResponse = (positiveResponse) => {
    setDeleteModalOpen(false);
    if (positiveResponse) {
      dispatch(setLoading());
      dispatch(getMonthlyStockData({ reportId }));
    }
  }

  const handleSaveClick = (e) => {
    dispatch(setLoading());
    dispatch(saveMonthlyStockData({ reportId }));
  }

  return (
    <ScrollSync>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={12}>
          <FormHeaderPaper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
            }}
            divider={false}
          >
            <SelectedFarm />
            <SelectedReport reportName={reportName} />
            <FormTitleTypography variant="h5" component="div">
              Livestock Monthly Stock Totals Tool
            </FormTitleTypography>
          </FormHeaderPaper>
        </Grid>

        <Grid item xs={12} sx={{ paddingTop: '0 !important' }}>
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column", paddingTop: '0' }}>
            <List >
              <ListSubheader sx={{ bgcolor: "background.paper" }}>
                <ScrollSyncPane>
                  <>
                  </>
                </ScrollSyncPane>
              </ListSubheader>

            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} sx={{ paddingTop: '0 !important' }}>
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column", paddingTop: '0' }}>
            <List sx={{ mb: 2 }}>
              <ListSubheader sx={{ bgcolor: "background.paper" }}>
                <ScrollSyncPane>
                  <>
                    <Grid container direction="row" >
                      <Grid item xs={4}>
                        <Button
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                          onClick={() => { navigate('/data-entry/livestock/numbers-weights'); }}>
                          Close livestock monthly stock totals tool
                        </Button>
                      </Grid>
                      <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Stack spacing={2} sx={{ marginLeft: 'auto', float: 'right' }} direction="row">
                          <Button
                            disabled={locked}
                            variant="outlined"
                            sx={{ textTransform: 'none' }}
                            onClick={() => setDeleteModalOpen(true)}>
                            Cancel
                          </Button>
                          <Button variant="outlined"
                            disabled={locked}
                            sx={{ textTransform: 'none' }}
                            onClick={handleSaveClick}>
                            Save
                          </Button>

                        </Stack>
                      </Grid>
                    </Grid>
                    <CustomizedDivider />
                  </>
                </ScrollSyncPane>
              </ListSubheader>
              {formData?.sectors?.map((sector, index) => (
                  <React.Fragment key={index}>
                    <DataEntryTable
                      key={sector.id}
                      sector={sector}
                      initialFormData={formData.sectors.find(s => s.sectorId === sector.sectorId) || {}}
                      firstVisibleCellDetails={firstVisibleCellDetails}
                      locked={locked}
                      sectorIndex={index}
                    />
                  </React.Fragment>
              ))}
            </List>
            <CustomizableCancelModal
              isOpen={deleteModalOpen}
              value='Choose Yes to lose all of the values you have entered in this session or choose No to continue.'
              handleResponse={(value) => {
                handleModalResponse(value);
              }}
            />
          </Paper>
        </Grid>
      </Grid >
    </ScrollSync>
  );
};
export default MonthlyStockTool;
